// @givmo/passkeys — the web's way back in (AS-WEB26): unlink the company with a code sent to the
// account's own email, so personal sign-in works again (the backend's escape hatch, AS-BE16).
//
// Drawn IN the door's own card (as each console's "Reset your password" and code prompts are), so it
// keeps the brand panel beside it and has an address a lock email can link to: each console renders
// it at `/lost-company-access`. Three steps, as the approved frames C1–C5 and D1–D6:
//   1. what unlinking does and costs, before anything is asked of the person; then the account's
//      address ("Email me a code", or "I already have a code");
//   2. the emailed code, the cost restated in one line right above the act, and the act;
//   3. done: "You can sign in again", back to the door.
//
// A host whose sign-in has its own register frames the flow itself (`frame`): Board's director door
// opens it in Board's shell, where the page's title is the step's title and the rest sits in the
// director's card — never the console's admin sign-in shell (cut 1007e's fold, Board's B-F3).
//
// It never names a company (signed out: whoever holds the password reads it), and every line about
// the code holds whether or not the address has an account (the request is a 202 for every address,
// the verify's 204 is uniform). Its states: a wrong, expired or spent code (one 400) clears the code
// and puts the person back in the field; too many tries (429) and no answer (a 5xx) leave the code,
// which is still good for its hour; the absent route (404) says this isn't available here now; the
// backend's 60-second resend interval is shown as a countdown, never discovered by a silent no-op.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Button, ErrorMessage, TextField, cn, testIdProp } from '@givmo/design-system';
import { ESCAPE_HATCH_WORDS as W } from './escape-hatch-copy';
import {
  escapeHatchFailureOf,
  requestEscapeHatchCode,
  verifyEscapeHatchCode,
  type EscapeHatchFailure,
} from './escape-hatch-transport';
import { keepNumbersWhole } from './keep-numbers-whole';
import { useStepTitleFocus } from './step-focus';

const PREFIX = 'escape-hatch';
const CODE_LENGTH = 6;

/**
 * The backend's `EMAIL_VERIFICATION_RESEND_MIN_INTERVAL_SECONDS` default: a request inside it mints
 * nothing, so the page waits it out visibly rather than letting a second press look like a send.
 */
export const ESCAPE_HATCH_RESEND_INTERVAL_MS = 60_000;

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:opacity-60';

const FAILURE_LINE: Record<EscapeHatchFailure, string> = {
  codeRejected: W.codeRejected,
  throttled: W.throttled,
  dark: W.dark,
  unavailable: W.unavailable,
};

type Step = 'request' | 'code' | 'done';

export interface EscapeHatchFlowProps {
  /** The door's own heading look, so the flow reads as that door's next screen. */
  headingClass: string;
  /** Back to the door it came from ("Back to sign in", on step 1 and when done). */
  onBackToSignIn: () => void;
  /** An address the door already holds, offered, never assumed. */
  initialEmail?: string;
  /**
   * A host register's frame (Board's director shell): it places the step's `title` as the page's
   * own title and `body` in its card. The title node carries the step focus, so keep it inside the
   * page's heading. Absent, the flow draws its own heading, as in a console's sign-in card.
   */
  frame?: (title: ReactNode, body: ReactNode) => ReactElement;
}

function TextLink({
  children,
  onClick,
  disabled,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(LINK, 'self-start')}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

/**
 * Milliseconds left until `until`, re-rendering four times a second while there are any.
 *
 * Armed even when `until` has passed by the time the effect runs (a page suspended between the render
 * and its effect, as a phone switched to the mail app is): its first tick redraws, which shows the way
 * to a new code, and stops. Arming nothing there left the count standing at 1:00 (AS-WEB36).
 */
function useCountdown(until: number | null): number {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (until === null) return undefined;
    const id = globalThis.setInterval(() => {
      setTick((tick) => tick + 1);
      if (until <= Date.now()) globalThis.clearInterval(id);
    }, 250);
    return () => globalThis.clearInterval(id);
  }, [until]);
  return until === null ? 0 : Math.max(0, until - Date.now());
}

/** "1:00", "0:58": the wait as the frames print it. */
function minutesSeconds(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function EscapeHatchFlow({
  headingClass,
  onBackToSignIn,
  initialEmail = '',
  frame,
}: EscapeHatchFlowProps) {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'requesting' | 'verifying' | null>(null);
  const [failure, setFailure] = useState<EscapeHatchFailure | null>(null);
  const [resent, setResent] = useState(false);
  // Opened on an existing code ("I already have a code"): nothing was just sent, so the step says so.
  const [withExistingCode, setWithExistingCode] = useState(false);
  const [resendAt, setResendAt] = useState<number | null>(null);
  const wait = useCountdown(resendAt);
  const title = useStepTitleFocus(step);
  const codeField = useRef<HTMLInputElement>(null);
  // Declared after the title's focus, so on the code step the field, not the title, keeps it.
  useLayoutEffect(() => {
    if (step === 'code') codeField.current?.focus();
  }, [step]);

  const trimmed = email.trim();

  const ask = useCallback(
    async (again: boolean) => {
      if (busy !== null || trimmed.length === 0) return;
      setBusy('requesting');
      setFailure(null);
      try {
        await requestEscapeHatchCode(trimmed);
        setResendAt(Date.now() + ESCAPE_HATCH_RESEND_INTERVAL_MS);
        setResent(again);
        setWithExistingCode(false);
        setCode('');
        setStep('code');
        if (again) codeField.current?.focus();
      } catch (thrown) {
        setFailure(escapeHatchFailureOf(thrown));
      } finally {
        setBusy(null);
      }
    },
    [busy, trimmed],
  );

  const unlink = useCallback(async () => {
    if (busy !== null || code.length !== CODE_LENGTH) return;
    setBusy('verifying');
    setFailure(null);
    try {
      await verifyEscapeHatchCode(trimmed, code);
      setStep('done');
    } catch (thrown) {
      const why = escapeHatchFailureOf(thrown);
      setFailure(why);
      setResent(false);
      // A refused code is wrong or spent: clear it and put the person back in the field. A failure
      // that is not about the code (too many tries, no answer) leaves it: it is still good.
      if (why === 'codeRejected') setCode('');
      codeField.current?.focus();
    } finally {
      setBusy(null);
    }
  }, [busy, code, trimmed]);

  const heading = (text: string) => (
    <h1 ref={title} tabIndex={-1} className={cn(headingClass, 'text-balance outline-none')}>
      {text}
    </h1>
  );

  /**
   * One step, in the console's card (its own heading over its lines) or in a host's frame (the title
   * handed to the frame, still the focus target; the lines head the body).
   */
  const shape = (testId: string, text: string, lines: ReactNode, rest: ReactNode) => {
    if (frame === undefined) {
      return (
        <div className="flex flex-col gap-20" {...testIdProp(testId)}>
          <div className="flex flex-col gap-4">
            {heading(text)}
            {lines}
          </div>
          {rest}
        </div>
      );
    }
    return frame(
      <span ref={title} tabIndex={-1} className="outline-none">
        {text}
      </span>,
      <div className="flex flex-col gap-20" {...testIdProp(testId)}>
        <div className="flex flex-col gap-4">{lines}</div>
        {rest}
      </div>,
    );
  };

  if (step === 'done') {
    return shape(
      `${PREFIX}-done`,
      W.doneTitle,
      <p className={LINE}>{W.doneBody}</p>,
      <Button
        appearance="saas"
        tone="primary"
        size="lg"
        fullWidth
        onClick={onBackToSignIn}
        testId={`${PREFIX}-done-back`}
      >
        {W.backToSignIn}
      </Button>,
    );
  }

  if (step === 'code') {
    return shape(
      `${PREFIX}-code-step`,
      W.codeTitle,
      <p className={cn(LINE, 'break-words')} {...testIdProp(`${PREFIX}-code-sent`)}>
        {keepNumbersWhole(
          withExistingCode || trimmed.length === 0 ? W.codeFromEmail : W.codeSent(trimmed),
        )}
      </p>,
      <>
        <form
          className="flex flex-col gap-16"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void unlink();
          }}
        >
          <div className="flex flex-col gap-8">
            <TextField
              ref={codeField}
              label={W.codeLabel}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              value={code}
              error={failure === 'codeRejected'}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, ''));
                setFailure(null);
              }}
              testId={`${PREFIX}-code`}
            />
            {failure !== null && (
              <ErrorMessage testId={`${PREFIX}-error`}>{FAILURE_LINE[failure]}</ErrorMessage>
            )}
            {resent && failure === null && (
              <p
                role="status"
                className="pt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-resent`)}
              >
                {W.resent}
              </p>
            )}
            {wait > 0 ? (
              <p
                className="pt-4 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
                {...testIdProp(`${PREFIX}-resend-wait`)}
              >
                {W.resendWait(minutesSeconds(wait))}
              </p>
            ) : (
              <div className="pt-4">
                <TextLink
                  disabled={busy !== null || trimmed.length === 0}
                  onClick={() => void ask(true)}
                  testId={`${PREFIX}-resend`}
                >
                  {W.resend}
                </TextLink>
              </div>
            )}
          </div>
          <p
            className="mt-8 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${PREFIX}-act-reminder`)}
          >
            {W.actReminder}
          </p>
          <Button
            type="submit"
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={busy === 'verifying'}
            disabled={code.length !== CODE_LENGTH || busy === 'requesting'}
            testId={`${PREFIX}-unlink`}
          >
            {W.act}
          </Button>
        </form>
        <TextLink
          disabled={busy !== null}
          onClick={() => {
            setFailure(null);
            setResent(false);
            setStep('request');
          }}
          testId={`${PREFIX}-back`}
        >
          {W.back}
        </TextLink>
      </>,
    );
  }

  return shape(
    `${PREFIX}-request-step`,
    W.title,
    <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-request-lines`)}>
      <p className={LINE}>{W.what}</p>
      <p className={LINE}>
        {W.cost} {W.matchRequests}
      </p>
      <p className={LINE}>{W.keep}</p>
    </div>,
    <>
      <form
        className="flex flex-col gap-16"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void ask(false);
        }}
      >
        <div className="flex flex-col gap-8">
          <TextField
            label={W.emailLabel}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setFailure(null);
            }}
            testId={`${PREFIX}-email`}
          />
          <p
            className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${PREFIX}-email-hint`)}
          >
            {W.emailHint}
          </p>
          {failure !== null && (
            <ErrorMessage testId={`${PREFIX}-error`}>{FAILURE_LINE[failure]}</ErrorMessage>
          )}
        </div>
        <Button
          type="submit"
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading={busy === 'requesting'}
          disabled={trimmed.length === 0}
          testId={`${PREFIX}-request`}
        >
          {W.request}
        </Button>
      </form>
      <div className="flex flex-col gap-8">
        <TextLink
          disabled={busy !== null || trimmed.length === 0}
          onClick={() => {
            setFailure(null);
            setResent(false);
            setWithExistingCode(true);
            setCode('');
            setStep('code');
          }}
          testId={`${PREFIX}-have-code`}
        >
          {W.haveCode}
        </TextLink>
        <TextLink
          disabled={busy !== null}
          onClick={onBackToSignIn}
          testId={`${PREFIX}-back-to-sign-in`}
        >
          {W.backToSignIn}
        </TextLink>
      </div>
    </>,
  );
}
